'use client';

import React, { useState } from 'react';
import GfSectionMark from '@/components/common/GfSectionMark';
import BookingLinkButton from '@/components/accommodation/BookingLinkButton';
import StayLinkButton from '@/components/accommodation/StayLinkButton';
import HotelCard from '@/components/accommodation/HotelCard';
import { t } from '@/i18n/translations';

/**
 * Секция «Проверенные отели» на странице города.
 * Данные приходят из Sanity (`cityPage.hotels`, см. src/sanity/queries/city.js):
 * документы типа `hotel`, привязанные к городу. Карточки в стиле Gallery·Field.
 *
 * Показываем первые 6 отелей; остальные — под кнопкой «Показать ещё». Ссылка
 * «Все варианты →» в шапке ведёт на отдельную страницу со всеми отелями + картой.
 * Городской поиск на Booking.com / Trip.com — кнопками под сеткой.
 */

const PREVIEW_COUNT = 6;

export default function CityHotels({
    hotels,
    lang = 'ru',
    countrySlug,
    citySlug,
    cityDisplayName,
    // Городские поисковые ссылки — те же, что у кнопок в разделе «Где остановиться».
    cityName,
    countryName,
    countryCurrency,
    countryData,
    cityData,
    affiliateProvider,
    hasStayButton = false,
}) {
    const ui = t(lang, 'accommodationUi');
    const list = (hotels || []).filter(Boolean);
    const [expanded, setExpanded] = useState(false);

    if (list.length === 0) return null;

    const visible = expanded ? list : list.slice(0, PREVIEW_COUNT);
    const hasMore = list.length > PREVIEW_COUNT;

    // «Все варианты →» в шапке — на страницу со всеми отелями города.
    const allHref = countrySlug && citySlug ? `/${lang}/${countrySlug}/${citySlug}/hotels` : null;

    return (
        <section className="gf-sec" id="hotels" style={{ paddingTop: 0 }}>
            <GfSectionMark
                eyebrow={ui.cityHotels.eyebrow(cityDisplayName)}
                title={ui.cityHotels.title}
                tag={String(list.length)}
                allHref={allHref}
                allLabel={ui.cityHotels.allLabel}
            />
            <p className="gf-lead">{ui.cityHotels.lead}</p>

            <div className="ct-hotels">
                {visible.map((h) => (
                    <HotelCard key={h._id} hotel={h} lang={lang} countrySlug={countrySlug} citySlug={citySlug} />
                ))}
            </div>

            {hasMore && !expanded && (
                <div className="gf-more-wrap">
                    <button type="button" className="gf-more" onClick={() => setExpanded(true)}>
                        {ui.cityHotels.showMore(list.length - PREVIEW_COUNT)}
                    </button>
                </div>
            )}

            {/* Городской поиск на Booking.com / Trip.com — под последней строкой с отелями */}
            {(cityName || hasStayButton) && (
                <div className="ho-foot">
                    <span className="ho-foot-label">{ui.cityHotels.findMore}</span>
                    <div className="ho-foot-btns">
                        {cityName && (
                            <BookingLinkButton
                                variant="bare"
                                className="ho-foot-btn"
                                cityName={cityName}
                                countryName={countryName}
                                countryCurrency={countryCurrency}
                                citySlug={citySlug}
                                siteLang={lang}
                                subIdSuffix="hotels-section"
                            >
                                {ui.cityHotels.searchBooking}
                            </BookingLinkButton>
                        )}
                        {!cityName && hasStayButton && (
                            <StayLinkButton
                                variant="bare"
                                className="ho-foot-btn ghost"
                                countrySlug={countrySlug}
                                citySlug={citySlug}
                                siteLang={lang}
                                countryData={countryData}
                                cityData={cityData}
                                affiliateProvider={affiliateProvider}
                                linkParams={{ mode: 'base', subId: `${citySlug}-hotels-section` }}
                            >
                                {ui.cityHotels.searchTrip}
                            </StayLinkButton>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
